import React, { useEffect, useRef } from "react";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { MdDelete, MdKeyboardBackspace } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { checkAcessRights } from "../../../_helper/helper";
import { DELETE_ACCESS, EDIT_ACCESS } from "../../../_contstants/common";
import {
  closeDialog,
  openDialog,
} from "../../../components/dialog/dialogSlice";
import {
  clearQuotationID,
  setQuotationID,
} from "../../quotation/quotationSlice";
import IconButton from "../../../components/common/IconButton";
import { LoadingPanel } from "../../../components/layout/Loading";
import AlertBox from "../../../components/common/AlertBox";
import { deleteQuotation } from "../../quotation/services/quotation.services";
import AppDialog, {
  DialogCloseButton,
} from "../../../components/dialog/Dialog";
import { getDashboardQuotations } from "../services/dashboard.services";

const DashboardQuotations: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);

  useEffect(() => {
    const payload = {
      FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
      ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
    };
    dispatch(getDashboardQuotations(payload));
  }, [dateFilter]);

  return (
    <>
      <DeleteQuotationDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton
              fillMode="solid"
              title="Back"
              onClick={() => navigate(-1)}
              icon={MdKeyboardBackspace}
              iconClassName="absolute-position"
              style={{ height: 30 }}
              iconStyle={{ fontSize: "20px" }}
            />
            <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
              Quotation List
            </Typography.h4>
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <QuotationGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const QuotationGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights("/quotation", EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights("/quotation", DELETE_ACCESS);
  const QuotationList = useAppSelector(
    (state) => state.dashboard.DashboardQuotationList
  );
  const loading = useAppSelector((state) => state.dashboard.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? QuotationList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (item: any) => {
      navigate("/quotation/edit", {
        state: {
          QuotationID: item?.ID,
        },
      });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteQuotation"));
      dispatch(setQuotationID(ID));
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {QuotationList && QuotationList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(QuotationList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : QuotationList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={QuotationList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="FirstName"
            title="Name"
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/quotation/edit", {
                        state: {
                          QuotationID: props.dataItem?.ID,
                        },
                      })
                    }
                  >
                    {props.dataItem?.FirstName} {props.dataItem?.LastName}
                  </div>
                ) : (
                  <div className="normalName">
                    {props.dataItem?.FirstName} {props.dataItem?.LastName}
                  </div>
                )}
              </td>
            )}
          />
          <Column field="MobileNo" title="Mobile No." />
          <Column field="EmailID" title="Email ID" />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteQuotationDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const QuotationID = useAppSelector((state) => state.quotation.QuotationID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const payload = {
        FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
        ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
      };
      const response = await dispatch(deleteQuotation(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getDashboardQuotations(payload));
        dispatch(closeDialog());
        dispatch(clearQuotationID());
      } else {
        dispatch(getDashboardQuotations(payload));
        dispatch(closeDialog());
        dispatch(clearQuotationID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteQuotation" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Quotation"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Quotation?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(QuotationID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default DashboardQuotations;
